import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Rate } from '../../ui/rate/Rate';

import styles from '../orderSummary/OrderSummary.module.css';
import messages from '../orderSummary/OrderSummary.messages';
import { FormattedCurrency } from '../../../iwIntlProvider/FormattedCurrency';
import { useGetFormalOffers } from '@api/lendingAPI/lapiHooks';
import {
    GetFormalOffersResponse,
    GetIwocapayPaymentCapturedResponse
} from '@iwoca/lapi-client/edge';
import { Skeleton } from '@iwoca/orion';
import { formatUnfundedCapturedPaymentDates } from '../dashboard/Dashboard.helpers';
import { mapOfferDurationToProduct } from '../dashboard/OfferChoiceForm/utils/mapOfferDurationToProduct';
import { CapturedPayment } from '../dashboard/Dashboard.types';
import { useDrawDownRepaymentScheduleIwocaPay } from '../dashboard/OfferChoiceForm/useDrawDownRepaymentScheduleIwocaPay';
import { RepaymentsSchedule } from '../orderSummary/RepaymentsSchedule';

export const NewOrderSummary = ({
    title,
    orderAmount,
    plan
}: {
    title: ReactNode;
    orderAmount: number;
    plan: CapturedPayment;
}) => {
    const { formalOffers, loadingFormalOffers } = useGetFormalOffers();
    const offer = getRelevantFormalOffer({
        formalOffers: formalOffers,
        sellerProduct: plan.sellerProduct
    });

    const {
        drawDownRepaymentScheduleIwocapay,
        interestRate,
        loadingDrawDownRepaymentScheduleIwocapay
    } = useDrawDownRepaymentScheduleIwocaPay({
        offer: offer!,
        payLink: plan.payLink
    });

    if (loadingDrawDownRepaymentScheduleIwocapay || loadingFormalOffers)
        return <Skeleton />;

    if (!offer) return null;
    if (!drawDownRepaymentScheduleIwocapay) return null;

    return (
        <div className={styles.summary}>
            <h2 className={styles.title}>
                {title ? (
                    title
                ) : (
                    <FormattedMessage {...messages.defaultTitle} />
                )}
            </h2>

            <h3 data-testid="order-amount-title" className={styles.subTitle}>
                <FormattedMessage
                    {...messages.subTitle}
                    values={{
                        amount: <FormattedCurrency value={orderAmount} />
                    }}
                />
            </h3>

            <RepaymentsSchedule
                schedule={drawDownRepaymentScheduleIwocapay!.schedule}
                totalInterest={
                    drawDownRepaymentScheduleIwocapay!.summary.total_interest!
                }
                totalPrinciple={drawDownRepaymentScheduleIwocapay!.schedule.reduce(
                    (total, schedule) => total + schedule.principal,
                    0
                )}
                totalPayable={
                    drawDownRepaymentScheduleIwocapay!.summary.total_payable!
                }
                overrideScheduleDates={formatUnfundedCapturedPaymentDates(plan)}
            />

            <p data-testid="interest-disclaimer" className={styles.disclaimer}>
                <FormattedMessage
                    {...messages.interestRateDisclaimer}
                    values={{
                        interestRate: <Rate value={interestRate} />
                    }}
                />
            </p>
        </div>
    );
};

const getRelevantFormalOffer = ({
    formalOffers,
    sellerProduct
}: {
    formalOffers: NonNullable<GetFormalOffersResponse['data']>['formal_offers'];
    sellerProduct: GetIwocapayPaymentCapturedResponse['data'][number]['product'];
}) => {
    if (!formalOffers) return null;

    return formalOffers.find((offer) => {
        const offerSellerProduct = mapOfferDurationToProduct(offer);
        return offerSellerProduct == sellerProduct;
    });
};
